<template>
  <div class="notification-center">
    <el-dropdown @command="handleCommand" trigger="click" ref="notificationDropdown">
      <el-badge :value="unreadCount" :max="99" class="notification-badge">
        <el-button
          icon="el-icon-bell"
          circle
          :class="{ 'connected': wsConnected }"
          class="notification-button"
        ></el-button>
      </el-badge>
      <el-dropdown-menu slot="dropdown" class="notification-dropdown">
        <div class="notification-header">
          <span class="notification-title">{{ $t('notificationCenter.title') }}</span>
          <el-button
            v-if="unreadCount > 0"
            type="text"
            size="mini"
            class="read-all-btn"
            @click.stop="markAllAsRead"
          >{{ $t('notificationCenter.markAllRead') }}</el-button>
        </div>
        
        <el-tabs v-model="activeTab" @tab-click="handleTabClick">
          <el-tab-pane :label="$t('notificationCenter.tabs.all')" name="all">
            <notification-list
              :notifications="notifications"
              :loading="loading"
              @mark-read="markAsRead"
            />
          </el-tab-pane>
          <el-tab-pane :label="$t('notificationCenter.tabs.appointment')" name="APPOINTMENT">
            <notification-list
              :notifications="appointmentNotifications"
              :loading="loading"
              @mark-read="markAsRead"
            />
          </el-tab-pane>
          <el-tab-pane :label="$t('notificationCenter.tabs.expiration')" name="EXPIRATION">
            <notification-list
              :notifications="expirationNotifications"
              :loading="loading"
              @mark-read="markAsRead"
            />
          </el-tab-pane>
          <el-tab-pane :label="$t('notificationCenter.tabs.promotion')" name="PROMOTION">
            <notification-list
              :notifications="promotionNotifications"
              :loading="loading"
              @mark-read="markAsRead"
            />
          </el-tab-pane>
        </el-tabs>
        
        <div class="notification-footer">
          <el-button type="text" size="small" @click.stop="viewAllNotifications">
            {{ $t('notificationCenter.viewAll') }}
          </el-button>
        </div>
      </el-dropdown-menu>
    </el-dropdown>
    
    <!-- 预约提醒弹窗 -->
    <appointment-alert
      :visible="appointmentAlertVisible"
      :appointment-data="currentAppointmentData"
      :alert-message="appointmentAlertMessage"
      @close="closeAppointmentAlert"
    />
    
    <!-- 通知详情对话框 -->
    <el-dialog
      :title="$t('notificationCenter.dialogTitle')"
      :visible.sync="dialogVisible"
      width="700px"
      class="notification-dialog"
      :modal="false"
      :z-index="100"
    >
      <el-table
        :data="tableData"
        border
        style="width: 100%"
        v-loading="tableLoading"
      >
        <el-table-column
          prop="title"
          :label="$t('notificationCenter.table.title')"
          width="180"
        ></el-table-column>
        <el-table-column
          prop="content"
          :label="$t('notificationCenter.table.content')"
        ></el-table-column>
        <el-table-column
          :label="$t('notificationCenter.table.type')"
          width="100"
        >
          <template slot-scope="scope">
            <el-tag :type="getTagType(scope.row.businessType)">
              {{ getTypeName(scope.row.businessType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="createTime"
          :label="$t('notificationCenter.table.time')"
          width="160"
          :formatter="formatDate"
        ></el-table-column>
        <el-table-column
          :label="$t('notificationCenter.table.status')"
          width="80"
        >
          <template slot-scope="scope">
            <el-tag :type="scope.row.isRead === 1 ? 'info' : 'danger'" size="mini">
              {{ scope.row.isRead === 1 ? $t('notificationCenter.status.read') : $t('notificationCenter.status.unread') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('notificationCenter.table.operation')"
          width="100"
        >
          <template slot-scope="scope">
            <el-button
              v-if="scope.row.isRead === 0"
              size="mini"
              type="primary"
              @click="markAsRead(scope.row.id)"
              icon="el-icon-check"
              circle
            ></el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      ></el-pagination>
    </el-dialog>
  </div>
</template>

<script>
import NotificationList from './NotificationList.vue';
import AppointmentAlert from './AppointmentAlert.vue';

export default {
  name: 'NotificationCenter',
  components: {
    NotificationList,
    AppointmentAlert
  },
  data() {
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      wsConnected: false,
      ws: null,
      activeTab: 'all',
      dialogVisible: false,
      tableData: [],
      tableLoading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      dropdownVisible: false,
      // 预约提醒弹窗相关
      appointmentAlertVisible: false,
      currentAppointmentData: {},
      appointmentAlertMessage: ''
    };
  },
  computed: {
    userId() {
      // 优先获取管理员ID，如果不存在则使用普通用户ID
      const adminId = this.$storage.get('adminId');
      const userInfo = this.$storage.get('userInfo');
      
      if (adminId) {
        return adminId;
      } else if (userInfo && typeof userInfo === 'string') {
        try {
          const parsed = JSON.parse(userInfo);
          return parsed.id;
        } catch (e) {
          console.error('解析用户信息失败', e);
          return null;
        }
      } else if (userInfo && userInfo.id) {
        return userInfo.id;
      } else if (this.$store.state.user && this.$store.state.user.id) {
      return this.$store.state.user.id;
      }
      
      console.warn('无法获取用户ID，通知功能可能不可用');
      return null;
    },
    filteredNotifications() {
      if (this.activeTab === 'all') {
        return this.notifications;
      }
      return this.notifications.filter(n => n.businessType === this.activeTab);
    },
    appointmentNotifications() {
      return this.notifications.filter(n => {
        const title = (n.title || '').toLowerCase();
        const content = (n.content || '').toLowerCase();
        return n.businessType === 'APPOINTMENT' || 
               title.includes('预约') || 
               content.includes('预约') ||
               title.includes('appointment') ||
               content.includes('appointment');
      });
    },
    expirationNotifications() {
      return this.notifications.filter(n => {
        const title = (n.title || '').toLowerCase();
        const content = (n.content || '').toLowerCase();
        return n.businessType === 'EXPIRATION' || 
               title.includes('到期') || 
               content.includes('到期') ||
               title.includes('会员') ||
               content.includes('会员') ||
               title.includes('expiration') ||
               content.includes('expiration');
      });
    },
    promotionNotifications() {
      return this.notifications.filter(n => {
        const title = (n.title || '').toLowerCase();
        const content = (n.content || '').toLowerCase();
        return n.businessType === 'PROMOTION' || 
               title.includes('活动') || 
               content.includes('活动') ||
               title.includes('促销') ||
               content.includes('促销') ||
               title.includes('优惠') ||
               content.includes('优惠') ||
               title.includes('promotion') ||
               content.includes('promotion');
      });
    }
  },
  created() {
    this.fetchUnreadCount();
    this.initializeWebSocket();
  },
  beforeDestroy() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close();
    }
  },
  methods: {
    // 通知去重处理（与 WebSocket onmessage 逻辑保持一致）
    deduplicateNotifications(notifications) {
      if (!notifications || !Array.isArray(notifications)) {
        return [];
      }
      
      const seenIds = new Set();
      const seen = new Map();
      const deduplicated = [];
      
      for (const notification of notifications) {
        const title = (notification.title || '').trim();
        const content = (notification.content || '').trim();
        
        // 优先使用 ID 去重
        if (notification.id) {
          if (seenIds.has(notification.id)) {
            continue;
          }
          seenIds.add(notification.id);
        }
        
        // 生成 2 分钟时间窗口键
        let timeKey = '';
        try {
          const time = notification.createTime || notification.timestamp || Date.now();
          const date = new Date(time);
          const minutes = Math.floor(date.getMinutes() / 2) * 2; // 2分钟窗口
          timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        } catch (e) {
          timeKey = 'unknown';
        }
        
        // 使用标题+内容+时间窗口作为去重键
        const dedupeKey = `${title}_${content}_${timeKey}`;
        
        if (!seen.has(dedupeKey)) {
          seen.set(dedupeKey, notification);
          deduplicated.push(notification);
        } else {
          // 如果有ID，保留ID更大的（更新的）
          const existing = seen.get(dedupeKey);
          if (notification.id && existing.id && notification.id > existing.id) {
            const index = deduplicated.findIndex(n => n.id === existing.id);
            if (index !== -1) {
              deduplicated[index] = notification;
              seen.set(dedupeKey, notification);
            }
          }
        }
      }
      
      console.log('去重前通知数量:', notifications.length, '去重后数量:', deduplicated.length);
      return deduplicated;
    },
    fetchUnreadCount() {
      if (!this.userId) {
        console.warn('用户ID为空，无法获取通知');
        return;
      }
      this.loading = true;
      this.$http.get(`notification/unread/${this.userId}`).then(res => {
        if (res.data.code === 0) {
          // 对未读通知进行去重
          const deduplicatedNotifications = this.deduplicateNotifications(res.data.data);
          this.unreadCount = deduplicatedNotifications.length;
        }
        this.loading = false;
      }).catch(err => {
        console.error('获取未读通知数量失败', err);
        this.loading = false;
      });
    },
    fetchNotifications() {
      if (!this.userId) {
        console.warn('用户ID为空，无法获取通知');
        return;
      }
      this.loading = true;
      this.$http.get('notification/list', {
        params: { 
          userId: this.userId,
          types: this.activeTab !== 'all' ? this.activeTab : null
        }
      }).then(res => {
        if (res.data.code === 0) {
          // 对通知列表进行去重
          const deduplicated = this.deduplicateNotifications(res.data.data);
          
          // 本地化每条通知
          this.notifications = deduplicated.map(notification => {
            const localized = this.localizeNotification(notification);
            return {
              ...notification,
              _originalTitle: notification.title,
              _originalContent: notification.content,
              title: localized.title,
              content: localized.content
            };
          });
        }
        this.loading = false;
      }).catch(err => {
        console.error('获取通知列表失败', err);
        this.loading = false;
      });
    },
    markAsRead(notificationId) {
      if (!this.userId) {
        console.warn('用户ID为空，无法标记已读');
        return;
      }
      this.$http.post(`notification/read/${notificationId}`, null, {
        params: { userId: this.userId }
      }).then(res => {
        if (res.data.code === 0) {
          this.notifications = this.notifications.map(n => 
            n.id === notificationId ? { ...n, isRead: 1 } : n
          );
          this.unreadCount = Math.max(0, this.unreadCount - 1);
          this.$message.success(this.$t('notificationCenter.messages.markReadSuccess'));
          
          // 如果在表格模式，也更新表格数据
          if (this.dialogVisible) {
            this.tableData = this.tableData.map(n => 
              n.id === notificationId ? { ...n, isRead: 1 } : n
            );
          }
        } else {
          this.$message.error(this.$t('notificationCenter.messages.markReadFailed'));
        }
      }).catch(err => {
        console.error('标记已读失败', err);
        this.$message.error(this.$t('notificationCenter.messages.markReadFailed'));
      });
    },
    markAllAsRead() {
      // 假设有批量标记为已读的接口
      this.$confirm(this.$t('notificationCenter.messages.markAllReadConfirm'), this.$t('common.tip'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        // 这里应该调用实际的批量标记API
        // 模拟批量标记成功
        this.notifications = this.notifications.map(n => ({ ...n, isRead: 1 }));
        this.unreadCount = 0;
        this.$message.success(this.$t('notificationCenter.messages.markAllReadSuccess'));
      }).catch(() => {});
    },
    initializeWebSocket() {
      if (!this.userId) {
        console.warn('用户ID为空，无法建立WebSocket连接');
        return;
      }
      const apiBase = (process.env.VUE_APP_API_BASE || '/springboot6hv37').replace(/\/$/, '')
      let origin
      try { origin = new URL(apiBase, window.location.origin).origin } catch (e) { origin = window.location.origin }
      const isHttps = origin.startsWith('https')
      const wsScheme = isHttps ? 'wss' : 'ws'
      const host = new URL(origin).host
      const wsUrl = `${wsScheme}://${host}/springboot6hv37/ws/notification?userId=${this.userId}`
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WebSocket连接已建立');
        this.wsConnected = true;
      };

      this.ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
          console.log('收到新通知', data);
        } catch (e) {
          console.error('解析通知数据失败', e);
          return;
        }
        
        // 优化的去重逻辑
        const title = (data.title || '').trim();
        const content = (data.content || '').trim();
        
        // 优先使用ID去重
        if (data.id && this.notifications.some(n => n.id === data.id)) {
          console.log('忽略重复通知 (ID匹配):', data.title, 'ID:', data.id);
          return;
        }
        
        // 生成2分钟时间窗口键（从5分钟缩小到2分钟，减少误判）
        let timeKey = '';
        try {
          const time = data.createTime || data.timestamp || Date.now();
          const date = new Date(time);
          const minutes = Math.floor(date.getMinutes() / 2) * 2; // 改为2分钟窗口
          timeKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}_${String(date.getHours()).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        } catch (e) {
          timeKey = 'unknown';
        }
        
        // 检查是否为预约通知
        const isAppointment = title.includes('预约') || content.includes('预约') || 
                              title.includes('Appointment') || content.includes('Appointment') ||
                              title.includes('นัดหมาย') || content.includes('นัดหมาย');
        
        let isDuplicate = false;
        
        if (isAppointment) {
          // 预约通知：必须标题+内容完全相同，且在2分钟窗口内才判定重复
          isDuplicate = this.notifications.some(notification => {
            const existingTitle = (notification.title || '').trim();
            const existingContent = (notification.content || '').trim();
            
            // 必须标题和内容都完全匹配
            if (existingTitle !== title || existingContent !== content) {
              return false;
            }
            
            try {
              const existingTime = notification.createTime || notification.timestamp || Date.now();
              const existingDate = new Date(existingTime);
              const existingMinutes = Math.floor(existingDate.getMinutes() / 2) * 2;
              const existingTimeKey = `${existingDate.getFullYear()}-${String(existingDate.getMonth() + 1).padStart(2, '0')}-${String(existingDate.getDate()).padStart(2, '0')}_${String(existingDate.getHours()).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')}`;
              return existingTimeKey === timeKey;
            } catch (e) {
              return false;
            }
          });
        } else {
          // 非预约通知：标题+内容+2分钟窗口
          isDuplicate = this.notifications.some(notification => {
            const existingTitle = (notification.title || '').trim();
            const existingContent = (notification.content || '').trim();
            
            if (existingTitle !== title || existingContent !== content) {
              return false;
            }
            
            try {
              const existingTime = notification.createTime || notification.timestamp || Date.now();
              const existingDate = new Date(existingTime);
              const existingMinutes = Math.floor(existingDate.getMinutes() / 2) * 2;
              const existingTimeKey = `${existingDate.getFullYear()}-${String(existingDate.getMonth() + 1).padStart(2, '0')}-${String(existingDate.getDate()).padStart(2, '0')}_${String(existingDate.getHours()).padStart(2, '0')}:${String(existingMinutes).padStart(2, '0')}`;
              return existingTimeKey === timeKey;
            } catch (e) {
              return false;
            }
          });
        }
        
        if (!isDuplicate) {
          // 本地化通知内容
          const localized = this.localizeNotification(data);
          
          // 添加本地化后的通知到列表（保留原始数据）
          const localizedData = {
            ...data,
            _originalTitle: data.title,
            _originalContent: data.content,
            title: localized.title,
            content: localized.content
          };
          
          this.notifications.unshift(localizedData);
          
          // 更新未读数量
          this.unreadCount += 1;
          
          // 强制更新视图
          this.$forceUpdate();
          
          console.log('✅ 添加新通知(已本地化):', localized.title, '当前通知数:', this.notifications.length);
          
          // 延迟显示弹窗，确保数据已更新
          this.$nextTick(() => {
            if (data.businessType === 'APPOINTMENT' || isAppointment) {
              setTimeout(() => {
                this.showAppointmentAlert(localizedData);
              }, 50);
            } else {
              this.$notify({
                title: localized.title,
                message: localized.content,
                type: 'info',
                duration: 5000
              });
            }
          });
        } else {
          console.log('忽略重复通知 (内容+时间匹配):', title);
        }
      };

      this.ws.onclose = () => {
        console.log('WebSocket连接已关闭');
        this.wsConnected = false;
        // 尝试重新连接
        setTimeout(this.initializeWebSocket, 5000);
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket错误', error);
        this.wsConnected = false;
      };
    },
    handleCommand(command) {
      if (command === 'fetchNotifications') {
        this.fetchNotifications();
      }
    },
    handleTabClick() {
      this.fetchNotifications();
    },
    viewAllNotifications() {
      this.dialogVisible = true;
      this.fetchTableData();
      // 关闭下拉菜单
      this.$refs.notificationDropdown.hide();
    },
    fetchTableData() {
      if (!this.userId) {
        console.warn('用户ID为空，无法获取通知数据');
        return;
      }
      this.tableLoading = true;
      this.$http.get('notification/list', {
        params: {
          userId: this.userId,
          page: this.currentPage,
          limit: this.pageSize
        }
      }).then(res => {
        if (res.data.code === 0) {
          // 对表格数据进行去重
          const deduplicated = this.deduplicateNotifications(res.data.data);
          
          // 本地化每条通知
          this.tableData = deduplicated.map(notification => {
            const localized = this.localizeNotification(notification);
            return {
              ...notification,
              _originalTitle: notification.title,
              _originalContent: notification.content,
              title: localized.title,
              content: localized.content
            };
          });
          this.total = this.tableData.length;
        }
        this.tableLoading = false;
      }).catch(err => {
        console.error('获取通知列表失败', err);
        this.tableLoading = false;
      });
    },
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchTableData();
    },
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchTableData();
    },
    formatDate(row, column) {
      const date = new Date(row.createTime);
      return date.toLocaleString();
    },
    getTagType(type) {
      switch (type) {
        case 'APPOINTMENT':
          return 'primary';
        case 'EXPIRATION':
          return 'warning';
        case 'PROMOTION':
          return 'success';
        default:
          return 'info';
      }
    },
    getTypeName(type) {
      switch (type) {
        case 'APPOINTMENT':
          return this.$t('notificationCenter.types.appointment');
        case 'EXPIRATION':
          return this.$t('notificationCenter.types.expiration');
        case 'PROMOTION':
          return this.$t('notificationCenter.types.promotion');
        default:
          return this.$t('notificationCenter.types.system');
      }
    },
    // 显示预约提醒弹窗
    showAppointmentAlert(notificationData) {
      // 解析通知数据，提取预约详情
      let appointmentData = {};
      try {
        // 如果通知包含额外数据
        if (notificationData.data) {
          const parsedData = typeof notificationData.data === 'string' 
            ? JSON.parse(notificationData.data) 
            : notificationData.data;
          appointmentData = {
            customerName: parsedData.customerName || parsedData.xingming || '未知客户',
            coachName: parsedData.coachName || parsedData.jiaolianxingming || '未知教练',
            appointmentTime: parsedData.appointmentTime || parsedData.startTime || notificationData.createTime,
            serviceName: parsedData.serviceName || parsedData.yuyueshuoming || '健身服务',
            amount: parsedData.amount || parsedData.price || '0',
            paymentMethod: parsedData.paymentMethod || '未知'
          };
        } else {
          // 从通知内容中尝试提取信息
          appointmentData = {
            customerName: '新客户',
            coachName: '教练',
            appointmentTime: notificationData.createTime,
            serviceName: '健身服务',
            amount: '0',
            paymentMethod: '未知'
          };
        }
      } catch (error) {
        console.error('解析预约数据失败', error);
        appointmentData = {
          customerName: '新客户',
          coachName: '教练',
          appointmentTime: notificationData.createTime,
          serviceName: '健身服务',
          amount: '0',
          paymentMethod: '未知'
        };
      }
      
      this.currentAppointmentData = appointmentData;
      this.appointmentAlertMessage = notificationData.content || this.$t('appointmentAlert.defaultMessage');
      this.appointmentAlertVisible = true;
    },
    // 关闭预约提醒弹窗
    closeAppointmentAlert() {
      this.appointmentAlertVisible = false;
      this.currentAppointmentData = {};
    },
    // 本地化通知内容（将后端中文通知转换为当前语言）
    localizeNotification(notification) {
      const title = (notification.title || '').trim();
      const content = (notification.content || '').trim();
      
      // 1. Admin端预约通知：客户 xxx 预约了教练 xxx 的 xxx 服务...
      if (title === '新预约通知' || title === 'New Appointment' || title === 'มีนัดหมายใหม่') {
        const customerMatch = content.match(/(?:客户|Customer|ลูกค้า)\s+([^\s]+)/);
        const coachMatch = content.match(/(?:教练|Coach|โค้ช)\s+([^\s]+)/);
        const serviceMatch = content.match(/(?:的\s+|for\s+|สำหรับ\s+)([^服务，]+?)(?:\s*服务|,|，|\s+Time|\s+เวลา)/);
        const timeMatch = content.match(/(?:预约时间：|Time:\s*|เวลา:\s*)([^，,]+)/);
        const paymentMatch = content.match(/(?:支付方式：|Payment:\s*|การชำระเงิน:\s*)([^，,]+)/);
        const amountMatch = content.match(/(?:金额：|Amount:\s*|จำนวนเงิน:\s*)¥?([0-9.]+)/);
        
        if (customerMatch && coachMatch) {
          return {
            title: this.$t('notificationCenter.templates.appointmentNotificationTitle'),
            content: this.$t('notificationCenter.templates.appointmentNotificationContent')
              .replace('{customer}', customerMatch[1])
              .replace('{coach}', coachMatch[1])
              .replace('{service}', serviceMatch ? serviceMatch[1].trim() : '')
              .replace('{time}', timeMatch ? timeMatch[1].trim() : '')
              .replace('{payment}', paymentMatch ? paymentMatch[1].trim() : '')
              .replace('{amount}', amountMatch ? amountMatch[1] : '0')
          };
        }
      }
      
      // 2. 用户端预约提醒：您有一个即将开始的预约...
      if (title === '预约提醒' || title === 'Appointment Reminder' || title === 'การแจ้งเตือนนัดหมาย') {
        if (content.includes('即将开始') || content.includes('upcoming') || content.includes('กำลังจะเริ่ม')) {
          return {
            title: this.$t('notificationCenter.templates.appointmentReminderTitle'),
            content: this.$t('notificationCenter.templates.appointmentReminderContent')
          };
        }
      }
      
      // 无法匹配，返回原内容
      return {
        title: notification.title,
        content: notification.content
      };
    }
  },
  mounted() {
    const apiBase = (process.env.VUE_APP_API_BASE || '/springboot6hv37').replace(/\/$/, '')
    let origin
    try { origin = new URL(apiBase, window.location.origin).origin } catch (e) { origin = window.location.origin }
    const isHttps = origin.startsWith('https')
    const wsScheme = isHttps ? 'wss' : 'ws'
    const host = new URL(origin).host
    const wsUrl = `${wsScheme}://${host}/springboot6hv37/ws/notification?userId=${this.userId}`
    // 使用 wsUrl 建立连接
    // this.socket = new WebSocket(wsUrl)
  }
};
</script>

<style>
.notification-center {
  display: inline-block;
}

.notification-badge {
  margin-right: 20px;
}

.notification-badge sup{
  top: 20px !important;
}

.notification-button {
  transition: all 0.3s;
}

.notification-button.connected {
  border-color: #67c23a;
}

.notification-dropdown {
  width: 350px;
  padding: 0;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;
  border-bottom: 1px solid #ebeef5;
}

.notification-title {
  font-weight: bold;
  font-size: 16px;
}

.notification-footer {
  text-align: center;
  padding: 10px;
  border-top: 1px solid #ebeef5;
}

.notification-dialog .el-dialog__body {
  padding: 10px 20px;
}

/* 强制处理弹窗z-index问题 */
.notification-dialog {
  z-index: 100 !important;
}

.notification-dialog .el-dialog {
  z-index: 100 !important;
}

.notification-dialog .el-dialog__wrapper {
  z-index: 100 !important;
}

/* 禁用或降低模态框背景层级 */
.v-modal {
  display: none !important;
  /* z-index: 99 !important;
  opacity: 0.1 !important; */
}

/* 预约提醒弹窗的z-index */
.appointment-alert-dialog {
  z-index: 1000 !important;
}

.appointment-alert-dialog .el-dialog {
  z-index: 1000 !important;
}

.appointment-alert-dialog .el-dialog__wrapper {
  z-index: 1000 !important;
}
</style> 